import { ArrowRight, ChevronRight, FileCheck2, FileText, PenLine, Plus, Sparkles, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader } from "@/components/common/loader";
import { openDocument } from "@/features/documents/api";
import { AdvisorWizard } from "@/features/notices/components/advisor/advisor-wizard";
import {
  buildNoticeActivity,
  NoticeActivityFeed,
} from "@/features/notices/components/notice-activity-feed";
import { NoticeCard } from "@/features/notices/components/notice-card";
import { NoticeFormDialog } from "@/features/notices/components/notice-form-dialog";
import { useClientNotices } from "@/features/notices/hooks/use-client-notices";
import type { Notice } from "@/features/notices/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

type FilterKey = "all" | "open" | "resolved";

const quickActions = [
  { icon: FileCheck2, title: "Request penalty abatement", subtitle: "First-time abatement letter" },
  { icon: PenLine, title: "File power of attorney (2848)", subtitle: "Send for e-signature" },
  { icon: FileText, title: "Pull wage & income transcript", subtitle: "Verify the notice figures" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Resolved notices sink to the bottom; the rest sort by soonest deadline. */
function byDeadline(a: Notice, b: Notice): number {
  const ar = a.status === "RESOLVED";
  const br = b.status === "RESOLVED";
  if (ar !== br) return ar ? 1 : -1;
  return (a.daysUntilDeadline ?? Infinity) - (b.daysUntilDeadline ?? Infinity);
}

export default function ClientIrsNoticesPage() {
  const { client } = useActiveClient();
  const navigate = useNavigate();
  const { notices, loading, error, refetch } = useClientNotices(client.id);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [prefillDef, setPrefillDef] = useState("");
  const [showAllActivity, setShowAllActivity] = useState(false);
  const clientType = client.type === "Business" ? "B" : "I";

  const responseDue = notices.filter((n) => n.status === "OPEN").length;
  const awaitingCount = notices.filter((n) => n.status === "IN_PROGRESS").length;
  const resolvedCount = notices.filter((n) => n.status === "RESOLVED").length;
  const openCount = responseDue + awaitingCount;

  const visible = useMemo(() => {
    const list =
      filter === "open"
        ? notices.filter((n) => n.status !== "RESOLVED")
        : filter === "resolved"
          ? notices.filter((n) => n.status === "RESOLVED")
          : notices;
    return [...list].sort(byDeadline);
  }, [notices, filter]);

  // Soonest upcoming deadline among unresolved notices → the "Next deadline" hero.
  const soonest = useMemo(() => {
    return notices
      .filter((n) => n.status !== "RESOLVED" && n.daysUntilDeadline !== null)
      .sort((a, b) => (a.daysUntilDeadline ?? 0) - (b.daysUntilDeadline ?? 0))[0];
  }, [notices]);

  const allActivity = useMemo(() => buildNoticeActivity(notices), [notices]);
  const activity = showAllActivity ? allActivity : allActivity.slice(0, 6);

  // 30-day response-window progress for the hero.
  const windowDays =
    soonest?.responseDeadline && soonest.receivedOn
      ? Math.max(
          1,
          Math.round(
            (new Date(`${soonest.responseDeadline}T00:00:00`).getTime() -
              new Date(`${soonest.receivedOn}T00:00:00`).getTime()) /
              86_400_000,
          ),
        )
      : null;
  const elapsed =
    windowDays !== null ? Math.min(windowDays, Math.max(0, windowDays - (soonest?.daysUntilDeadline ?? 0))) : null;

  const filters: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: "All", count: notices.length },
    { key: "open", label: "Open", count: openCount },
    { key: "resolved", label: "Resolved", count: resolvedCount },
  ];

  const dueSuffix =
    soonest && soonest.daysUntilDeadline !== null && soonest.daysUntilDeadline >= 0
      ? ` in ${soonest.daysUntilDeadline} days`
      : "";

  function viewDocument(id: string) {
    openDocument(id).catch(() => toast.error("Couldn't open the document."));
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Title + actions */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">IRS notices</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <strong className="text-warning font-semibold">
              {responseDue} response{responseDue === 1 ? "" : "s"} due{dueSuffix}
            </strong>{" "}
            · {awaitingCount} awaiting the IRS · {resolvedCount} resolved.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-[42px]" onClick={() => setAdvisorOpen(true)}>
            <Sparkles /> Notice advisor
          </Button>
          <Button variant="outline" className="h-[42px]" onClick={() => setAddOpen(true)}>
            <Upload /> Upload notice
          </Button>
          <Button variant="cta" className="h-[42px]" onClick={() => setAddOpen(true)}>
            <Plus /> Add notice
          </Button>
        </div>
      </div>

      {/* Body: primary feed (2/3) + analytical rail (1/3) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Primary feed */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          {/* Filter row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="group" aria-label="Filter notices" className="bg-muted flex gap-0.5 rounded-[12px] p-[3px]">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    "h-[34px] rounded-[9px] px-3.5 text-[13px] transition-colors",
                    filter === f.key
                      ? "bg-card text-foreground font-semibold shadow-sm"
                      : "text-muted-foreground hover:text-foreground font-medium",
                  )}
                >
                  {f.label} · {f.count}
                </button>
              ))}
            </div>
            <span className="text-muted-foreground text-[13px]">Sorted by deadline</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : error ? (
            <p className="text-destructive text-sm">{error}</p>
          ) : visible.length === 0 ? (
            <p className="text-muted-foreground text-sm">No IRS notices logged for this client yet.</p>
          ) : (
            <>
              {visible.map((notice) => (
                <NoticeCard
                  key={notice.id}
                  notice={notice}
                  clientId={client.id}
                  onViewDocument={viewDocument}
                />
              ))}

              {/* Notice activity */}
              <Card className="mt-1.5 gap-3.5 p-6">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-tight">Notice activity</h2>
                  {allActivity.length > 6 && (
                    <button
                      type="button"
                      onClick={() => setShowAllActivity((v) => !v)}
                      className="text-info text-[13px] font-semibold hover:underline"
                    >
                      {showAllActivity ? "Show less" : "All activity"}
                    </button>
                  )}
                </div>
                <NoticeActivityFeed events={activity} />
              </Card>
            </>
          )}
        </div>

        {/* Analytical rail */}
        <div className="flex flex-col gap-6 lg:col-span-1">
          {/* Next deadline hero */}
          {soonest && (
            <Card className="bg-brand-panel relative gap-3.5 overflow-hidden border-transparent p-6 text-white">
              <div
                aria-hidden
                className="bg-primary/25 pointer-events-none absolute -top-16 -right-16 size-56 rounded-full blur-3xl"
              />
              <span className="text-brand-panel-muted relative text-xs font-semibold tracking-wider uppercase">
                Next deadline
              </span>
              <div className="relative flex items-baseline gap-2.5">
                <span className="font-heading text-[52px] leading-none font-semibold tabular-nums">
                  {Math.abs(soonest.daysUntilDeadline ?? 0)}
                </span>
                <span className="text-brand-panel-muted text-base font-medium">
                  {(soonest.daysUntilDeadline ?? 0) < 0 ? "days overdue" : "days left"}
                </span>
              </div>
              <span className="relative text-sm">
                <strong className="font-semibold">{soonest.noticeCode} response</strong> · due{" "}
                <span className="tabular-nums">{formatDate(soonest.responseDeadline)}</span>
              </span>
              {windowDays !== null && elapsed !== null && (
                <>
                  <div className="relative h-1.5 overflow-hidden rounded-[3px] bg-white/15">
                    <div
                      className="bg-primary h-full rounded-[3px]"
                      style={{ width: `${Math.round((elapsed / windowDays) * 100)}%` }}
                    />
                  </div>
                  <span className="text-brand-panel-muted relative text-xs">
                    Day {elapsed} of the {windowDays}-day response window
                  </span>
                </>
              )}
              <Button
                asChild
                className="text-brand-panel relative h-[42px] bg-white hover:bg-white/90"
              >
                <Link to={`/client/${client.id}/irs-notices/${soonest.id}`}>
                  Open response draft <ArrowRight />
                </Link>
              </Button>
            </Card>
          )}

          {/* Summary */}
          <Card className="gap-3.5 p-6">
            <h2 className="text-lg font-semibold tracking-tight">Summary</h2>
            <div className="bg-muted flex h-2 gap-0.5 overflow-hidden rounded-full">
              {responseDue > 0 && <span className="bg-warning" style={{ flex: responseDue }} />}
              {awaitingCount > 0 && <span className="bg-info" style={{ flex: awaitingCount }} />}
              {resolvedCount > 0 && <span className="bg-success" style={{ flex: resolvedCount }} />}
            </div>
            <div className="flex flex-col gap-2.5 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <span className="bg-warning size-2 rounded-full" />
                  Response due
                </span>
                <strong className="font-semibold tabular-nums">{responseDue}</strong>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <span className="bg-info size-2 rounded-full" />
                  Awaiting IRS
                </span>
                <strong className="font-semibold tabular-nums">{awaitingCount}</strong>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <span className="bg-success size-2 rounded-full" />
                  Resolved
                </span>
                <strong className="font-semibold tabular-nums">{resolvedCount}</strong>
              </div>
            </div>
          </Card>

          {/* Quick actions */}
          <Card className="gap-1.5 p-[18px_14px]">
            <h2 className="px-2.5 text-lg font-semibold tracking-tight">Quick actions</h2>
            {quickActions.map((action) => (
              <button
                key={action.title}
                type="button"
                className="hover:bg-muted flex items-center gap-3 rounded-[12px] p-2.5 text-left transition-colors"
              >
                <span className="bg-accent text-info flex size-9 shrink-0 items-center justify-center rounded-[11px]">
                  <action.icon className="size-4" />
                </span>
                <span className="flex flex-grow flex-col leading-snug">
                  <span className="text-foreground font-semibold">{action.title}</span>
                  <span className="text-muted-foreground text-xs">{action.subtitle}</span>
                </span>
                <ChevronRight className="text-muted-foreground size-4" />
              </button>
            ))}
          </Card>
        </div>
      </div>

      <NoticeFormDialog
        clientId={client.id}
        notice={null}
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) setPrefillDef("");
        }}
        onSaved={refetch}
        defaultNoticeDefinition={prefillDef}
      />

      <AdvisorWizard
        clientId={client.id}
        clientType={clientType}
        open={advisorOpen}
        onOpenChange={setAdvisorOpen}
        onLogNotice={(definitionId) => {
          setPrefillDef(definitionId);
          setAddOpen(true);
        }}
        onOpened={(caseId) => navigate(`/client/${client.id}/cases/${caseId}`)}
      />
    </div>
  );
}
